"use client"

import { PlusIcon } from "@heroicons/react/20/solid"
import Button from "@src/components/Button"
import { NetworkIcon } from "@src/components/DefuseSDK/components/Network/NetworkIcon"
import type { ContactNetwork } from "@src/components/DefuseSDK/features/contacts/utils/contactNetwork"
import { getContactNetworkDisplayInfo } from "@src/components/DefuseSDK/features/contacts/utils/contactNetworkDisplay"
import ListItem from "@src/components/ListItem"
import { WalletSolidIcon } from "@src/icons"
import { motion, useReducedMotion } from "framer-motion"
import { enterTransition, ITEM_STAGGER, ITEMS_DELAY } from "./landingMotion"

type PreviewContact = {
  name: string
  address: string
  network: ContactNetwork
  color: {
    background: string
    icon: string
  }
}

const contacts: readonly PreviewContact[] = [
  {
    name: "Binance Deposit",
    address: "0x9f1d...72ac",
    network: "eth",
    color: { background: "#DBEAFE", icon: "#2563EB" },
  },
  {
    name: "Alex",
    address: "0x71c2...4e80",
    network: "eth",
    color: { background: "#DCFCE7", icon: "#16A34A" },
  },
  {
    name: "Maya",
    address: "7Yh9k...Q2mL",
    network: "solana",
    color: { background: "#EDE9FE", icon: "#7C3AED" },
  },
  {
    name: "Company Treasury",
    address: "bc1qxy...7s8e",
    network: "bitcoin",
    color: { background: "#FFEDD5", icon: "#EA580C" },
  },
  {
    name: "James",
    address: "0xa602...19bc",
    network: "base",
    color: { background: "#FEE2E2", icon: "#DC2626" },
  },
  {
    name: "Savings Wallet",
    address: "savings.near",
    network: "near",
    color: { background: "#CCFBF1", icon: "#0D9488" },
  },
]

export function ContactsHeader() {
  return (
    <header className="pointer-events-none flex items-center justify-between gap-4 pb-7">
      <h2 className="flex h-9 items-center font-medium text-gray-900 text-xl/7 tracking-tight">
        Contacts
      </h2>

      <Button variant="primary">
        <PlusIcon className="size-4 shrink-0" />
        Create contact
      </Button>
    </header>
  )
}

export function ContactsContent() {
  const reduceMotion = useReducedMotion()
  const animateEnter = !reduceMotion

  return (
    <div className="pointer-events-none space-y-1">
      {contacts.map((contact, index) => (
        <motion.div
          key={contact.address}
          {...enterTransition(ITEMS_DELAY + index * ITEM_STAGGER, animateEnter)}
        >
          <ContactPreviewRow contact={contact} />
        </motion.div>
      ))}
    </div>
  )
}

export default function LandingContactsPreview() {
  return (
    <section className="mx-auto h-full max-w-lg overflow-hidden p-8">
      <ContactsHeader />
      <ContactsContent />
    </section>
  )
}

function ContactPreviewRow({ contact }: { contact: PreviewContact }) {
  const displayInfo = getContactNetworkDisplayInfo(contact.network)

  return (
    <ListItem>
      <div
        className="-outline-offset-1 flex size-10 shrink-0 items-center justify-center rounded-full bg-gray-200 outline-1 outline-gray-900/10"
        style={{ backgroundColor: contact.color.background }}
      >
        <WalletSolidIcon
          className="size-5 text-gray-500"
          style={{ color: contact.color.icon }}
        />
      </div>

      <ListItem.Content>
        <ListItem.Title className="line-clamp-1">{contact.name}</ListItem.Title>
        <ListItem.Subtitle>{contact.address}</ListItem.Subtitle>
      </ListItem.Content>

      <ListItem.Content align="end">
        <ListItem.Title className="flex items-center gap-1">
          <span className={displayInfo.capitalize ? "capitalize" : undefined}>
            {displayInfo.label}
          </span>
          <NetworkIcon chainIcon={displayInfo.icon} sizeClassName="size-4" />
        </ListItem.Title>
      </ListItem.Content>
    </ListItem>
  )
}
